import { useCallback, useEffect, useRef, useState } from "react";
import { FiRefreshCw } from "react-icons/fi";
import { IoMdCloseCircle } from "react-icons/io";
import { formatCargoLabel, formatElapsed, formatTime, getInitials } from "../../utils.js";
import { getConnectedUsers } from "../../services/getConnectedUsers.js";
import styles from "./css/connectedUsersModal.module.css";


// MODAL DE USUÁRIOS CONECTADOS. SEM POLLING: CARREGA UMA VEZ A CADA ABERTURA E DE NOVO QUANDO O ADMIN CLICA EM "RECARREGAR".
// `stillOpen` VEM DO POLLING DA LISTA (A EMPRESA AINDA TEM TURNO ABERTO?)
export default function ConnectedUsersModal({ show, company, stillOpen, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [loadedAt, setLoadedAt] = useState(null);

    // DESCARTA RESPOSTAS ATRASADAS (MODAL FECHADO, OUTRA EMPRESA OU RECARGA MAIS NOVA)
    const requestId = useRef(0);

    const companyId = company?.companyId;

    // INVALIDA QUALQUER REQUISIÇÃO EM VOO (SEUS RESULTADOS PASSAM A SER DESCARTADOS)
    const discardPending = useCallback(() => {
        requestId.current += 1;
    }, []);


    const load = useCallback(async () => {
        if (!companyId) return;

        const current = ++requestId.current;

        try {
            setLoading(true);

            const res = await getConnectedUsers({ companyId });

            if (current !== requestId.current) return;

            setData(res);
            setError(null);
            setLoadedAt(new Date());

        } catch (e) {
            if (current !== requestId.current) return;

            console.error("ERROR - GET CONNECTED USERS:", e);
            setError(e?.response?.data?.error || "Nao foi possivel carregar os usuarios conectados no momento.");

        } finally {
            if (current === requestId.current) setLoading(false);
        }
    }, [companyId]);


    useEffect(() => {
        if (!show || !companyId) return;

        // CADA ABERTURA COMEÇA DO ZERO
        setData(null);
        setError(null);
        setLoadedAt(null);

        load();

        return discardPending;

    }, [show, companyId, load, discardPending]);


    if (!show || !company) return null;

    const users = data?.users ?? [];
    const localAppConnected = data ? data.localAppConnected : company.localAppConnected;

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
                <header className={styles.modalHeader}>
                    <div className={styles.headerTexts}>
                        <h2 className={styles.modalTitle}>{company.name}</h2>
                        <p className={styles.modalSubtitle}>
                            Turno #{company.sequenceNumber} · aberto há {formatElapsed(company.openedAt)}
                        </p>
                    </div>

                    <div className={styles.headerActions}>
                        <button
                            type="button"
                            className={styles.reloadButton}
                            onClick={load}
                            disabled={loading}
                            title="Consultar os usuários conectados novamente"
                        >
                            <FiRefreshCw size={15} className={loading ? styles.spinningIcon : undefined} />
                            Recarregar
                        </button>

                        <button type="button" className={styles.closeButton} onClick={onClose}>
                            <IoMdCloseCircle size={24} color="#2f3e49" />
                        </button>
                    </div>
                </header>

                <div className={styles.badgesRow}>
                    <span className={`${styles.badge} ${users.length > 0 ? styles.badgeOn : styles.badgeOff}`}>
                        {data ? `${users.length} ${users.length === 1 ? "usuário conectado" : "usuários conectados"}` : "Consultando..."}
                    </span>

                    <span className={`${styles.badge} ${localAppConnected ? styles.badgeOn : styles.badgeOff}`}>
                        {localAppConnected ? "App local conectado" : "App local desconectado"}
                    </span>

                    {!company.operating && <span className={`${styles.badge} ${styles.badgeBlocked}`}>Bloqueada</span>}
                </div>

                {!stillOpen && (
                    <div className={styles.closedNotice}>
                        Turno encerrado. Esta empresa não está mais na lista de abertos.
                    </div>
                )}

                <div className={styles.modalBody}>
                    {loading && !data ? (
                        <p className={styles.feedbackText}>Carregando usuários conectados...</p>

                    ) : error && !data ? (
                        <p className={styles.feedbackText}>{error}</p>

                    ) : data && users.length === 0 ? (
                        <p className={styles.feedbackText}>Nenhum usuário conectado no momento.</p>

                    ) : (
                        <>
                            {error && (
                                <p className={styles.errorNotice}>
                                    Falha ao recarregar. Mostrando os dados de {formatTime(loadedAt)}.
                                </p>
                            )}

                            <ul className={styles.userList}>
                                {users.map((user) => (
                                    <li key={user.userId} className={styles.userItem}>
                                        <div className={styles.userAvatar}>{getInitials(user.name)}</div>

                                        <div className={styles.userInfo}>
                                            <span className={styles.userName}>{user.name}</span>
                                            <span className={styles.userEmail}>{user.email ?? "—"}</span>
                                        </div>

                                        <span className={styles.cargoBadge}>{formatCargoLabel(user.cargo)}</span>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </div>

                <footer className={styles.modalFooter}>
                    {loadedAt
                        ? `Consultado às ${formatTime(loadedAt)}. Clique em Recarregar para consultar novamente.`
                        : "Consultando..."}
                </footer>
            </div>
        </div>
    );
}
